import { useFeature } from "@Light/app/features";
import { RequireNoService } from "@Light/components/app/RequireNoService";
import { useAccount, useLocation } from "@Light/components/page/account";
import { OptOutFilter, ServiceEvent } from "@Light/services/lightTypes";
import { WithPlan } from "@Light/utils/plan";
import dayjs from "dayjs";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { WithHeader } from "../page/header";
import { Confirmation } from "./Confirmation/Confirmation";
import { FAQ } from "./FAQ";
import { EnrollmentFlow } from "./flow/EnrollmentFlow";
import { maybeRequirementValue } from "./flow/screen";
import { OptOutSurveyProvider } from "@Light/components/optOutSurvey";
import {
  addressFirstEnrollmentScreens,
  zipFirstEnrollmentScreens,
} from "./screens";
import { SupportBottomSheetProvider } from "@Light/components/page/SupportBottomSheet";

export type EnrollProps = {
  planUUID?: string;
  serviceActivePath?: string;
  onLogout?: (source?: string, force?: boolean) => void;
};

export const Enroll: React.FC<EnrollProps> = ({
  planUUID,
  serviceActivePath,
  onLogout,
}) => {
  return (
    <RequireNoService redirect={serviceActivePath || "/"}>
      <SupportBottomSheetProvider>
        <EnrollContent planUUID={planUUID} onLogout={onLogout} />
      </SupportBottomSheetProvider>
    </RequireNoService>
  );
};

type EnrollContentProps = {
  planUUID?: string;
  onLogout?: (source?: string, force?: boolean) => void;
};

const EnrollContent: React.FC<EnrollContentProps> = ({
  planUUID,
  onLogout,
}) => {
  const account = useAccount();
  const { requirements } = account.enrollment;
  const serviceEvent =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
    (maybeRequirementValue(requirements.service_event)?.value as
      ServiceEvent | undefined) ?? ServiceEvent.Switch;
  const effectivePlanUUID =
    planUUID ?? maybeRequirementValue(requirements.plan)?.group_uuid;

  const filter: OptOutFilter =
    serviceEvent === ServiceEvent.MoveIn
      ? OptOutFilter.EnrollmentMoveIn
      : OptOutFilter.EnrollmentSwitch;

  const routes = (
    <WithPlan planUUID={effectivePlanUUID}>
      <div className="box-border h-full overflow-hidden">
        <Routes>
          <Route
            path="/faq"
            element={
              <WithHeader>
                <FAQ />
              </WithHeader>
            }
          />
          <Route path="/confirmed" element={<EnrollmentConfirmedRoute />} />
          <Route
            path="/*"
            element={
              <WithHeader>
                <EnrollFlow />
              </WithHeader>
            }
          />
        </Routes>
      </div>
    </WithPlan>
  );

  if (!onLogout) {
    return routes;
  }

  return (
    <OptOutSurveyProvider
      filter={filter}
      onConfirmClose={(source) => {
        onLogout(source ?? "opt_out_survey", true);
      }}
    >
      {routes}
    </OptOutSurveyProvider>
  );
};

const EnrollFlow = () => {
  const account = useAccount();
  const navigate = useNavigate();
  const addressFirst = useFeature("CUSTOMER_CLIENT_ADDRESS_FIRST_ENROLLMENT");

  if (account.enrollment.is_enrollment_finalized) {
    return <Navigate to="/enroll/confirmed" replace />;
  }

  return (
    <EnrollmentFlow
      basePath="enroll"
      screens={
        addressFirst ? addressFirstEnrollmentScreens : zipFirstEnrollmentScreens
      }
      onFinalize={() => navigate("/enroll/confirmed")}
    />
  );
};

const EnrollmentConfirmedRoute = () => {
  const location = useLocation();
  const dueDate = dayjs(location.service_start_date).format("MMMM DD, YYYY");
  return <Confirmation immediateStartProps={{ dueDate, exitTo: "/close" }} />;
};
